'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDashedBottomProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDashedBottom - Dashed grid pattern with bottom fade
 *
 * Uses CSS mask compositing to create a dashed intersection pattern
 * that fades from the bottom. Inverse of GridDashedTop, ideal for
 * CTA sections or footers.
 *
 * Fixed fadeSize: 0.50
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDashedBottom />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDashedBottom({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDashedBottomProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.5
  const sizeX = `${fadeSize * 100}%`
  const sizeY = `${fadeSize * 100 * 0.85}%` // Slightly shorter vertically
  const maskGradient = `
    repeating-linear-gradient(to right, black 0px, black 3px, transparent 3px, transparent 8px),
    repeating-linear-gradient(to bottom, black 0px, black 3px, transparent 3px, transparent 8px),
    radial-gradient(ellipse ${sizeX} ${sizeY} at 50% 100%, #000 45%, transparent 100%)
  `

  return (
    <motion.div
      className={cn('absolute inset-0 z-0 dark:opacity-70', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, var(--border) 1px, transparent 1px),
          linear-gradient(to bottom, var(--border) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 0',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
